import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateGameMutation ,
  useCreateDepartmentMutation,
  useGetPrinterQuery,
} from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { IPrinter } from "../../../Api/Interface/api.interface";
import NewPrinter from "../../Printer/Components/NewPopUpPrinter";
import AddIcon from "@mui/icons-material/Add";
import { useStyles } from "../../../theme/appStyles";
import CustomSwitch from "../../../Components/Switch/CustomSwitch";
import moment from "moment-timezone";

const Index = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [newLocation, { isLoading }] = useCreateGameMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const timezones: string[] = moment.tz.names();

  const formik = useFormik({
   initialValues: {
    name: "",
    display_name: "",
    short_description: "",
    description: "",
    pricing_type: "", // 'per_person', 'per_unit', 'fixed'
    min_kids: "",           // empty string instead of 0
    max_kids: "",
    min_adults: "",
    max_adults: "",
    free_kids_included: "",
    free_adults_included: "",
    extra_kids_allowed: false,   // empty or false by default
    extra_adults_allowed: false,
    kid_price: "",
    adult_price: "",
    extra_kid_price: "",
    extra_adult_price: "",
    shared_capacity: "",
    max_date_changes: "",
    refund_after_changes: false,
    isActive: true,
    viewOnline: true,
    viewPos: true,
  },
    onSubmit: async (values, { resetForm }) => {
      try {
        const response = await newLocation(values).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          resetForm();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const formValid = useMemo(() => {
         return formik.values.name === "" ||
           formik.values.name === undefined
           ? false
           : true;
       }, [formik]);

  const gridHeight = isMobile ? "30px" : "400px";

  return (
    <div>
      <Grid container spacing={3}>
        <Grid item lg={3} md={3} sm={12} xs={12}>
          <Card className={classes.stepperCard}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ borderBottom: 2, padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                pt={2}
                alignContent={"center"}
              >
                <Typography className={classes.cardTitle}>
                  Create Game
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item lg={9} md={9} sm={12} xs={12}>
          <form onSubmit={formik.handleSubmit}>
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Card className={classes.card}>
                  <Grid
                    container
                    justifyContent={"space-between"}
                    spacing={5}
                    sx={{ borderBottom: 2, padding: 2 }}
                    className={classes.titleGrid}
                  >
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      pt={2}
                      alignContent={"center"}
                    >
                      <Typography className={classes.cardTitle}>
                        Game Information
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container spacing={3} p={"24px"}>
                    <Grid item lg={6} md={6} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">
                             Name <span style={{ color: "red" }}>*</span>
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <TextField
                            placeholder="Enter Name"
                            size="small"
                            {...formik.getFieldProps("name")}
                            className={classes.textField}
                            InputProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                            InputLabelProps={{
                              sx: {
                                fontSize: 14,
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>

                   
<Grid item lg={6} md={6} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Display Name</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        placeholder="Enter Display Name"
        size="small"
        {...formik.getFieldProps("display_name")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={12} md={12} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Short Description</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        placeholder="Enter Short Description"
        size="small"
        multiline
        rows={2}
        {...formik.getFieldProps("short_description")}
        className={classes.discriptionTextField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={12} md={12} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Description</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        placeholder="Enter Description"
        size="small"
        multiline
        rows={4}
        {...formik.getFieldProps("description")}
        className={classes.discriptionTextField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={6} md={6} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Pricing Type <span style={{ color: "red" }}>*</span></Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        select
        fullWidth
        size="small"
        value={formik.values.pricing_type}
        onChange={(e) => formik.setFieldValue("pricing_type", e.target.value)}
      >
        <MenuItem value="">Select Type</MenuItem>
        <MenuItem value="per_person">Per Person</MenuItem>
        <MenuItem value="per_unit">Per Unit</MenuItem>
        <MenuItem value="fixed">Fixed</MenuItem>
      </TextField>
    </Grid>
  </Grid>
</Grid>

{/* <Grid item lg={6} md={6} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Shared Capacity</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        placeholder="Shared Capacity"
        size="small"
        {...formik.getFieldProps("shared_capacity")}
        className={classes.textField}
        inputProps={{ min: "0" }}
      />
    </Grid>
  </Grid>
</Grid> */}



<Grid item lg={6} md={6} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Max Date Changes</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("max_date_changes")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>
                   

                    

                    <Grid item lg={4} md={4} sm={12} xs={12}>
                      <Grid container alignItems="center">
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Typography variant="subtitle1">Active</Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <CustomSwitch
                            color="primary"
                            sx={{ mr: 2 }}
                            formik={formik}
                            name="isActive"
                          />
                        </Grid>
                      </Grid>
                    </Grid>

  <Grid item lg={4} md={4} sm={3} xs={6}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                               View POS 
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <CustomSwitch
                              color="primary"
                              sx={{ mr: 2 }}
                              formik={formik}
                              name="viewOnline"
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                        <Grid item lg={4} md={4} sm={3} xs={6}>
                        <Grid container alignItems="center">
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Typography variant="subtitle1">
                              View Online
                            </Typography>
                          </Grid>
                          <Grid item lg={12} md={12} sm={12} xs={12}>
                            <CustomSwitch
                              color="primary"
                              sx={{ mr: 2 }}
                              formik={formik}
                              name="viewPos"
                            />
                          </Grid>
                        </Grid>
                      </Grid>



                  </Grid>
                </Card>
              </Grid>



<Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                        Kids Information
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                    

                  
<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Min Kids</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("min_kids")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Max Kids</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("max_kids")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>



<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Free Kids Included</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("free_kids_included")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>



<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Extra Kids Allowed</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <CustomSwitch
        formik={formik}
        name="extra_kids_allowed"
        color="primary"
      />
    </Grid>
  </Grid>
</Grid>



<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Kid Price</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("kid_price")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>



<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Extra Kid Price</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("extra_kid_price")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>

           



                     
                    </Grid>
                  </Card>
                </Grid>

                <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                  <Card className={classes.card}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                        Adults Information
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} p={"24px"}>
                    

                  
<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Min Adults</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("min_adults")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Max Adults</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("max_adults")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>
                      


<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Free Adults Included</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("free_adults_included")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>

<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Extra Adults Allowed</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <CustomSwitch
        formik={formik}
        name="extra_adults_allowed"
        color="primary"
      />
    </Grid>
  </Grid>
</Grid>


<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Adult Price</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("adult_price")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>


<Grid item lg={3} md={3} sm={12} xs={12}>
  <Grid container alignItems="center">
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="subtitle1">Extra Adult Price</Typography>
    </Grid>
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <TextField
        type="number"
        size="small"
        {...formik.getFieldProps("extra_adult_price")}
        className={classes.textField}
      />
    </Grid>
  </Grid>
</Grid>
           
                     
                    </Grid>
                  </Card>
                </Grid>







              <Grid item lg={12} md={12} sm={12} xs={12} pt={3}>
                <Card className={classes.card}>
                  <Grid container spacing={3} p={"24px"}>
                    <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      sx={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        className={classes.addButton}
                        onClick={() => formik.resetForm()}
                      >
                        Cancel
                      </Button>
                      <Box m={0.5}></Box>

                      <Button
                        variant="contained"
                        startIcon={<SaveAltIcon />}
                        className={classes.addButton}
                        onClick={() => formik.handleSubmit()}
                        disabled={!formValid || isLoading}
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </form>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
